"use client";

import {
  MODULE_ALIGN_VALUES,
  MODULE_SPACING_VALUES,
  MODULE_STYLE_DEFAULTS,
  ModulesArray,
  type ModuleStyle,
} from "@/types/modules";
import React, { JSX, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../animated-card-diagram";
import { useDndMonitor, useDroppable, type Active } from "@dnd-kit/core";
import { Plus } from "lucide-react";
import { renderModuleSkeleton } from "./module-skeleton";
import { footerHeightFor } from "@/utils/splitPages";
import { HEADER_HEIGHT } from "@/config/layout";
import ModuleAreaChart from "./modules/charts/module-area-chart";
import ModuleBarChart from "./modules/charts/module-bar-chart";
import ModuleLineChart from "./modules/charts/module-line-chart";
import ModulePieChart from "./modules/charts/module-pie-chart";
import ModuleScatterChart from "./modules/charts/module-scatter-chart";
import ModuleRadialBarChart from "./modules/charts/module-radial-bar-chart";
import ModuleCandlestickChart from "./modules/charts/module-candlestick-chart";
import ModuleFunnelChart from "./modules/charts/module-funnel-chart";
import ModuleHeatmapChart from "./modules/charts/module-heatmap-chart";
import ModuleProfitLossChart from "./modules/charts/module-profit-loss-chart";
import ModuleRadarChart from "./modules/charts/module-radar-chart";
import ModuleSankeyChart from "./modules/charts/module-sankey-chart";
import ModuleSunburstChart from "./modules/charts/module-sunburst-chart";
import ModuleDottedMap from "./modules/module-dotted-map";
import ModuleFeedback from "./modules/module-feedback";
import ModuleImage from "./modules/module-image";
import ModuleIntro from "./modules/module-intro";
import ModuleProgressBars from "./modules/module-progress";
import ModulePyramid from "./modules/module-pyramid";
import ModuleQuote from "./modules/module-quote";
import ModuleTOC from "./modules/module-toc";
import ModuleProgressStages from "./modules/module-progress-stages";
import ModuleNodeCycle from "./modules/module-node-cycle";
import ModuleSegmentedCycle from "./modules/module-segmented-cycle";
import ModuleEcosystemMap from "./modules/module-ecosystem-map";
import ModuleHierarchyTree from "./modules/module-hierarchy-tree";
import ModuleParallelCategoryTrees from "./modules/module-parallel-category-trees";
import ModuleActivityFeed from "./modules/module-activity-feed";
import ModuleMetricList from "./modules/module-metric-list";
import ModuleDeltaComparison from "./modules/module-delta-comparison";
import ModuleSplitComparison from "./modules/module-split-comparison";
import ModuleSocialProofStats from "./modules/module-social-proof-stats";
import ModuleListCards from "./modules/module-list-cards";
import ModuleBigNumberChart from "./modules/module-big-number-chart";
import ModulePricingTiers from "./modules/module-pricing-tiers";
import ModuleRoiCalculator from "./modules/module-roi-calculator";
import ModuleLogoWall from "./modules/module-logo-wall";
import ModuleBeforeAfterAnnotated from "./modules/module-before-after-annotated";
import ModuleBeforeAfterSlider from "./modules/module-before-after-slider";
import ModuleForkJoin from "./modules/module-fork-join";
import ModuleGanttPhases from "./modules/module-gantt-phases";
import ModuleParallelStreams from "./modules/module-parallel-streams";
import ModuleSegmentedProgress from "./modules/module-segmented-progress";
import ModuleStateMachine from "./modules/module-state-machine";
import ModuleTimelineStepper from "./modules/module-timeline-stepper";
import ModuleQuadrantMatrix from "./modules/module-quadrant-matrix";
import ModuleCorrelationHeatmap from "./modules/module-correlation-heatmap";
import ModuleFeatureComparison from "./modules/module-feature-comparison";
import ModuleDataTable from "./modules/module-data-table";
import ModuleRingKpis from "./modules/module-ring-kpis";
import ModuleMetricTabsChart from "./modules/module-metric-tabs-chart";
import ModuleBreakdown from "./modules/module-breakdown";
import ModuleDualMetric from "./modules/module-dual-metric";
import ModuleStageBars from "./modules/module-stage-bars";
import ModuleInsightPanel from "./modules/module-insight-panel";
import ModuleSkillLevelMatrix from "./modules/module-skill-level-matrix";
import ModuleKeywordsGrouped from "./modules/module-keywords-grouped";
import ModuleKeywordsExplained from "./modules/module-keywords-explained";
import ModuleKeyTakeaways from "./modules/module-key-takeaways";
import ModuleFaq from "./modules/module-faq";
import { accentOffset, normalizeAccent } from "./modules/module-accent";
import {
  ModuleRevealGateProvider,
  type ModuleRevealGateState,
} from "@/components/motion/module-reveal-gate";
import { useRevealMemory } from "@/components/motion/reveal-memory";
import { useInView } from "motion/react";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { ModuleSlice, sliceOf } from "@/utils/moduleSplitContract";

const headingTSX = (title: string | undefined): JSX.Element | null => {
  const clean = stripInlineMarkdown(title);
  if (!clean) return null;
  return <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-100">{clean}</h2>;
};

export interface RenderModuleOptions {
  slice?: ModuleSlice;
}

type ResolvedModuleStyle = Required<ModuleStyle>;

const MODULE_SPACING_CLASS: Record<ResolvedModuleStyle["spacing"], string> = {
  compact: "gap-1",
  default: "gap-2",
  relaxed: "gap-3 py-2",
};

const MODULE_ALIGN_CLASS: Record<ResolvedModuleStyle["align"], string> = {
  left: "",
  center: "text-center",
};

type ModuleFrameLayout = "flow" | "intro" | "feedback" | "faq";

const MODULE_FRAME_LAYOUT_CLASS: Record<ModuleFrameLayout, string> = {
  flow: "",
  intro: "justify-center h-full",
  feedback: "justify-between flex-1 min-h-0",
  faq: "min-h-0 flex-1",
};

const pickOption = <T extends string>(
  value: unknown,
  options: readonly T[],
  fallback: T,
): T => (options.includes(value as T) ? (value as T) : fallback);

export const resolveModuleStyle = (module: any): ResolvedModuleStyle => ({
  accent: accentOffset(module?.accent),
  spacing: pickOption(module?.spacing, MODULE_SPACING_VALUES, MODULE_STYLE_DEFAULTS.spacing),
  align: pickOption(module?.align, MODULE_ALIGN_VALUES, MODULE_STYLE_DEFAULTS.align),
  showTitle:
    typeof module?.showTitle === "boolean"
      ? module.showTitle
      : MODULE_STYLE_DEFAULTS.showTitle,
});

const frameModule = (
  module: any,
  content: React.ReactNode,
  layout: ModuleFrameLayout = "flow",
  withTitle: boolean = true,
): JSX.Element => {
  const style = resolveModuleStyle(module);
  return (
    <div
      className={cn(
        "flex flex-col",
        MODULE_SPACING_CLASS[style.spacing],
        MODULE_ALIGN_CLASS[style.align],
        MODULE_FRAME_LAYOUT_CLASS[layout],
      )}
    >
      {withTitle && style.showTitle && module.title && headingTSX(module.title)}
      {content}
    </div>
  );
};

export const renderModule = (
  module: any,
  index: number,
  className?: string,
  options?: RenderModuleOptions,
) => {
  const slice = options?.slice ?? sliceOf(module);
  const accent = normalizeAccent(module.accent);

  switch (module.type) {
    case "line":
      return frameModule(
        module,
        <ModuleLineChart
          data={module.data}
          xAxisLabel={module.xAxisLabel}
          yAxisLabel={module.yAxisLabel}
          thresholds={module.thresholds}
          series={module.series}
          showBrush={module.showBrush}
          showGrid={module.showGrid}
          showMarkers={module.showMarkers}
          showLegend={module.showLegend}
          fadeEdges={module.fadeEdges}
          curve={module.curve}
          referenceArea={module.referenceArea}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "bar":
      return frameModule(
        module,
        <ModuleBarChart
          data={module.data}
          xAxisLabel={module.xAxisLabel}
          yAxisLabel={module.yAxisLabel}
          thresholds={module.thresholds}
          series={module.series}
          showBrush={module.showBrush}
          showGrid={module.showGrid}
          showMarkers={module.showMarkers}
          showLegend={module.showLegend}
          fadeEdges={module.fadeEdges}
          variant={module.variant}
          view3d={module.view3d}
          referenceArea={module.referenceArea}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "pie":
      return frameModule(
        module,
        <ModulePieChart data={module.data} grayscale={module.grayscale} accent={accent} className={className} />,
      );
    case "scatter":
      return frameModule(
        module,
        <ModuleScatterChart
          data={module.data}
          xAxisLabel={module.xAxisLabel}
          yAxisLabel={module.yAxisLabel}
          xAxisUnit={module.xAxisUnit}
          yAxisUnit={module.yAxisUnit}
          zAxisLabel={module.zAxisLabel}
          zAxisUnit={module.zAxisUnit}
          zAxisRange={module.zAxisRange}
          thresholds={module.thresholds}
          showBrush={module.showBrush}
          showGrid={module.showGrid}
          showMarkers={module.showMarkers}
          showLegend={module.showLegend}
          fadeEdges={module.fadeEdges}
          referenceArea={module.referenceArea}
          view3d={module.view3d}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "area":
      return frameModule(
        module,
        <ModuleAreaChart
          data={module.data}
          xAxisLabel={module.xAxisLabel}
          yAxisLabel={module.yAxisLabel}
          thresholds={module.thresholds}
          series={module.series}
          showBrush={module.showBrush}
          showGrid={module.showGrid}
          showMarkers={module.showMarkers}
          showLegend={module.showLegend}
          fadeEdges={module.fadeEdges}
          curve={module.curve}
          referenceArea={module.referenceArea}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "image":
      return frameModule(
        module,
        module.data && (
          <ModuleImage data={module.data} grayscale={module.grayscale} className={className} />
        ),
        "flow",
        false,
      );
    case "progress":
      return frameModule(
        module,
        module.data && (
          <ModuleProgressBars
            data={module.data}
            grayscale={module.grayscale}
            accent={accent}
            className={className}
          />
        ),
      );
    case "quote":
      return frameModule(
        module,
        module.data && (
          <ModuleQuote
            data={module.data}
            grayscale={module.grayscale}
            accent={accent}
            className={className}
          />
        ),
      );
    case "intro":
      return frameModule(
        module,
        <ModuleIntro
          title={module.title}
          summary={module.summary}
          author={module.author}
          noSkeleton={module.noSkeleton}
          grayscale={module.grayscale}
        />,
        "intro",
        false,
      );
    case "toc":
      return frameModule(
        module,
        module.data && <ModuleTOC data={module.data} slice={slice} />,
      );
    case "feedback":
      return frameModule(
        module,
        <ModuleFeedback grayscale={module.grayscale} accent={accent} />,
        "feedback",
      );
    case "pyramid":
      return frameModule(
        module,
        <ModulePyramid
          data={module.data}
          variant={module.variant}
          inverted={module.inverted}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "dotted-map":
      return frameModule(
        module,
        <ModuleDottedMap data={module.data} grayscale={module.grayscale} accent={accent} className={className} />,
      );
    case "radial-bar":
      return frameModule(
        module,
        <ModuleRadialBarChart data={module.data} grayscale={module.grayscale} accent={accent} className={className} />,
      );
    case "candlestick":
      return frameModule(
        module,
        <ModuleCandlestickChart
          data={module.data}
          xAxisLabel={module.xAxisLabel}
          yAxisLabel={module.yAxisLabel}
          showBrush={module.showBrush}
          grayscale={module.grayscale}
          className={className}
        />,
      );
    case "funnel":
      return frameModule(
        module,
        <ModuleFunnelChart data={module.data} grayscale={module.grayscale} accent={accent} className={className} />,
      );
    case "heatmap":
      return frameModule(
        module,
        <ModuleHeatmapChart data={module.data} view3d={module.view3d} grayscale={module.grayscale} className={className} />,
      );
    case "profit-loss":
      return frameModule(
        module,
        <ModuleProfitLossChart
          data={module.data}
          xAxisLabel={module.xAxisLabel}
          yAxisLabel={module.yAxisLabel}
          showMarkers={module.showMarkers}
          curve={module.curve}
          grayscale={module.grayscale}
          className={className}
        />,
      );
    case "radar":
      return frameModule(
        module,
        <ModuleRadarChart
          data={module.data}
          showMarkers={module.showMarkers}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "sankey":
      return frameModule(
        module,
        <ModuleSankeyChart data={module.data} grayscale={module.grayscale} accent={accent} className={className} />,
      );
    case "sunburst":
      return frameModule(
        module,
        <ModuleSunburstChart data={module.data} grayscale={module.grayscale} accent={accent} className={className} />,
      );
    case "progress-stages":
      return frameModule(
        module,
        <ModuleProgressStages
          data={module.data}
          showLegend={module.showLegend}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "fork-join":
      return frameModule(
        module,
        <ModuleForkJoin
          data={module.data}
          showLegend={module.showLegend}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "gantt-phases":
      return frameModule(
        module,
        <ModuleGanttPhases
          data={module.data}
          showLegend={module.showLegend}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "parallel-streams":
      return frameModule(
        module,
        <ModuleParallelStreams
          data={module.data}
          showLegend={module.showLegend}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "segmented-progress":
      return frameModule(
        module,
        <ModuleSegmentedProgress
          data={module.data}
          showLegend={module.showLegend}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "state-machine":
      return frameModule(
        module,
        <ModuleStateMachine
          data={module.data}
          showLegend={module.showLegend}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "timeline-stepper":
      return frameModule(
        module,
        <ModuleTimelineStepper
          data={module.data}
          showLegend={module.showLegend}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "quadrant-matrix":
      return frameModule(
        module,
        <ModuleQuadrantMatrix
          data={module.data}
          showLegend={module.showLegend}
          view3d={module.view3d}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "correlation-heatmap":
      return frameModule(
        module,
        <ModuleCorrelationHeatmap data={module.data} view3d={module.view3d} grayscale={module.grayscale} accent={accent} className={className} />,
      );
    case "feature-comparison":
      return frameModule(
        module,
        <ModuleFeatureComparison
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "table":
      return frameModule(
        module,
        <ModuleDataTable
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "ring-kpis":
      return frameModule(
        module,
        <ModuleRingKpis
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "metric-tabs-chart":
      return frameModule(
        module,
        <ModuleMetricTabsChart
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "breakdown":
      return frameModule(
        module,
        <ModuleBreakdown
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "dual-metric":
      return frameModule(
        module,
        <ModuleDualMetric
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "stage-bars":
      return frameModule(
        module,
        <ModuleStageBars
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "insight-panel":
      return frameModule(
        module,
        <ModuleInsightPanel
          data={module.data}
          seed={module.id}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "skill-level-matrix":
      return frameModule(
        module,
        <ModuleSkillLevelMatrix
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "node-cycle":
      return frameModule(
        module,
        <ModuleNodeCycle
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "segmented-cycle":
      return frameModule(
        module,
        <ModuleSegmentedCycle
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "ecosystem-map":
      return frameModule(
        module,
        <ModuleEcosystemMap
          data={module.data}
          activeText={module.activeText}
          inactiveText={module.inactiveText}
          activeShort={module.activeShort}
          availableShort={module.availableShort}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "hierarchy-tree":
      return frameModule(
        module,
        <ModuleHierarchyTree
          data={module.data}
          variant={module.variant}
          columns={module.columns}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "parallel-category-trees":
      return frameModule(
        module,
        <ModuleParallelCategoryTrees
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "activity-feed":
      return frameModule(
        module,
        <ModuleActivityFeed
          data={module.data}
          variant={module.variant}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "metric-list":
      return frameModule(
        module,
        <ModuleMetricList
          data={module.data}
          variant={module.variant}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "delta-comparison":
      return frameModule(
        module,
        <ModuleDeltaComparison
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "split-comparison":
      return frameModule(
        module,
        <ModuleSplitComparison
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "social-proof-stats":
      return frameModule(
        module,
        <ModuleSocialProofStats
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "list-cards":
      return frameModule(
        module,
        <ModuleListCards
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "big-number-chart":
      return frameModule(
        module,
        <ModuleBigNumberChart
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "pricing-tiers":
      return frameModule(
        module,
        <ModulePricingTiers
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "roi-calculator":
      return frameModule(
        module,
        <ModuleRoiCalculator
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "logo-wall":
      return frameModule(
        module,
        <ModuleLogoWall
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "before-after-annotated":
      return frameModule(
        module,
        <ModuleBeforeAfterAnnotated
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "before-after-slider":
      return frameModule(
        module,
        <ModuleBeforeAfterSlider
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "keywords-grouped":
      return frameModule(
        module,
        <ModuleKeywordsGrouped
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "keywords-explained":
      return frameModule(
        module,
        <ModuleKeywordsExplained
          data={module.data}
          grayscale={module.grayscale}
          accent={accent}
          className={className}
        />,
      );
    case "key-takeaways":
      return frameModule(
        module,
        module.data && (
          <ModuleKeyTakeaways
            data={module.data}
            grayscale={module.grayscale}
            accent={accent}
            className={className}
          />
        ),
      );
    case "faq":
      return frameModule(
        module,
        module.data && (
          <ModuleFaq
            data={module.data}
            grayscale={module.grayscale}
            accent={accent}
            className={className}
          />
        ),
        "faq",
      );
    default:
      return null;
  }
};

export type EditorInsertAnchor =
  | { before: string }
  | { after: string }
  | null;

export type EditorCanvasDropData =
  | { kind: "editor-page"; slideId: string }
  | { kind: "editor-insert-slot"; slideId: string; anchor: EditorInsertAnchor }
  | { kind: "editor-module"; slideId: string; moduleId: string; moduleType: string };

export interface EditorInsertTarget {
  slideId: string;
  anchor: EditorInsertAnchor;
  pageIndex?: number;
}

function sameInsertAnchor(a: EditorInsertAnchor, b: EditorInsertAnchor): boolean {
  if (!a || !b) return a === b;
  if ("before" in a) return "before" in b && a.before === b.before;
  return "after" in b && a.after === b.after;
}

function matchesInsertTarget(
  target: EditorInsertTarget | null | undefined,
  slot: EditorInsertTarget,
): boolean {
  return (
    !!target &&
    target.slideId === slot.slideId &&
    (target.pageIndex === undefined || target.pageIndex === slot.pageIndex) &&
    sameInsertAnchor(target.anchor, slot.anchor)
  );
}

export const EDITOR_MODULE_MOVE_PREFIX = "move-";

export interface EditorModuleMoveDragData {
  source: "device-module";
  slideId: string;
  moduleId: string;
}

export const EDITOR_MEDIA_DRAG_PREFIX = "media-";

const INSERT_SLOT_DRAG_PREFIXES = ["demo-", EDITOR_MODULE_MOVE_PREFIX, EDITOR_MEDIA_DRAG_PREFIX];

function movedModuleId(active: Active | null): string | null {
  const data = active?.data.current as Partial<EditorModuleMoveDragData> | undefined;
  return data?.source === "device-module" && data.moduleId ? data.moduleId : null;
}

export const EditorDndScope = createContext(false);

function EditorModuleDragFade({
  moduleId,
  children,
}: {
  moduleId: string;
  children: React.ReactNode;
}) {
  const insideEditorDnd = useContext(EditorDndScope);
  if (!insideEditorDnd) return <>{children}</>;
  return <EditorModuleDragFadeInner moduleId={moduleId}>{children}</EditorModuleDragFadeInner>;
}

function EditorModuleDragFadeInner({
  moduleId,
  children,
}: {
  moduleId: string;
  children: React.ReactNode;
}) {
  const [dragging, setDragging] = useState(false);
  const listener = useMemo(
    () => ({
      onDragStart: ({ active }: { active: Active }) =>
        setDragging(movedModuleId(active) === moduleId),
      onDragEnd: () => setDragging(false),
      onDragCancel: () => setDragging(false),
    }),
    [moduleId],
  );
  useDndMonitor(listener);

  return (
    <div className={cn("transition-opacity", dragging && "opacity-40")}>
      {children}
    </div>
  );
}

function EditorSlideDropZone({
  slideId,
  pageIndex,
  children,
}: {
  slideId: string;
  pageIndex?: number;
  children: React.ReactNode;
}) {
  const data: EditorCanvasDropData = { kind: "editor-page", slideId };
  const { setNodeRef } = useDroppable({
    id: `page-${slideId}-${pageIndex ?? 0}`,
    data,
  });

  return (
    <div
      ref={setNodeRef}
      className="relative flex flex-col gap-3 h-full min-h-[100px]"
    >
      {children}
    </div>
  );
}

function EditorInsertSlot({
  slideId,
  pageIndex,
  slot,
  anchor,
  className,
  onInsertClick,
  activeInsertTarget,
}: {
  slideId: string;
  pageIndex?: number;
  slot: number;
  anchor: EditorInsertAnchor;
  className?: string;
  onInsertClick?: (target: EditorInsertTarget) => void;
  activeInsertTarget?: EditorInsertTarget | null;
}) {
  const insideEditorDnd = useContext(EditorDndScope);
  const data: EditorCanvasDropData = {
    kind: "editor-insert-slot",
    slideId,
    anchor,
  };
  const { setNodeRef, isOver, active } = useDroppable({
    id: `slot-${slideId}-${pageIndex ?? 0}-${slot}`,
    data,
  });
  const dragging = !!active;
  const dragVisible =
    isOver &&
    dragging &&
    INSERT_SLOT_DRAG_PREFIXES.some((prefix) => String(active.id).startsWith(prefix));
  const slotTarget: EditorInsertTarget = { slideId, anchor, pageIndex };
  const clickable = insideEditorDnd && !!onInsertClick && !!anchor && !dragging;
  const pinned = clickable && matchesInsertTarget(activeInsertTarget, slotTarget);

  return (
    <div
      ref={setNodeRef}
      aria-hidden={clickable ? undefined : true}
      className={cn("pointer-events-none absolute inset-x-0 z-20 h-0", className)}
    >
      {clickable && (
        <button
          type="button"
          data-insert-slot=""
          aria-label="Insert module here"
          onClick={(event) => {
            event.stopPropagation();
            onInsertClick?.(slotTarget);
          }}
          className="group/insert pointer-events-auto absolute inset-x-0 top-0 h-3 -translate-y-1/2 cursor-pointer outline-none"
        >
          <span
            className={cn(
              "pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center opacity-0 transition-opacity group-hover/insert:opacity-100 group-focus-visible/insert:opacity-100",
              pinned && "opacity-100",
            )}
          >
            <span className="h-px flex-1 bg-primary/50" />
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Plus className="size-3" />
            </span>
            <span className="h-px flex-1 bg-primary/50" />
          </span>
        </button>
      )}
      {dragVisible && (
        <div className="absolute inset-x-0 top-0 flex -translate-y-1/2 items-center">
          <div className="h-px flex-1 bg-primary" />
          <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Plus className="size-3" />
          </div>
          <div className="h-px flex-1 bg-primary" />
        </div>
      )}
    </div>
  );
}

function EditorImageDropTarget({
  slideId,
  pageIndex,
  moduleId,
}: {
  slideId: string;
  pageIndex?: number;
  moduleId: string;
}) {
  const data: EditorCanvasDropData = {
    kind: "editor-module",
    slideId,
    moduleId,
    moduleType: "image",
  };
  const { setNodeRef, isOver, active } = useDroppable({
    id: `module-${slideId}-${pageIndex ?? 0}-${moduleId}`,
    data,
  });
  const highlighted =
    isOver && !!active && String(active.id).startsWith(EDITOR_MEDIA_DRAG_PREFIX);

  return (
    <div ref={setNodeRef} aria-hidden className="pointer-events-none absolute inset-0 z-20">
      {highlighted && (
        <div className="absolute -inset-1.5 flex items-center justify-center rounded-lg bg-primary/5 outline-1 outline-primary">
          <span className="rounded-[4px] bg-primary px-1.5 py-px text-[11px] font-medium text-primary-foreground">
            Replace image
          </span>
        </div>
      )}
    </div>
  );
}

function isContinuationSlice(module: any): boolean {
  const slice = sliceOf(module);
  return !!slice && slice.start > 0;
}

function centersLoneModule(modules: any[] | null | undefined): boolean {
  return !!modules && modules.length === 1 && !isContinuationSlice(modules[0]);
}

function isScrollbarPointer(event: React.PointerEvent<HTMLElement>): boolean {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;
  const { clientWidth, clientHeight, scrollWidth, scrollHeight } = target;
  if (clientWidth === 0 || clientHeight === 0) return false;
  const { offsetX, offsetY } = event.nativeEvent;
  return (
    (scrollWidth > clientWidth && offsetY > clientHeight) ||
    (scrollHeight > clientHeight && offsetX > clientWidth)
  );
}

export default function HandyoutPage({
  slideId,
  totalPages,
  pageIndex,
  initModules = null,
  className,
  currentPage,
  mode = "interactive",
  onModuleClick,
  onEmptyClick,
  onInsertClick,
  activeInsertTarget,
  selectedModuleId,
  selectedModuleLoading,
}: {
  sessionId?: string;
  slideId: string;
  initModules: ModulesArray | null;
  children?: React.ReactNode;
  title?: string;
  totalPages?: number;
  className?: string;
  currentPage?: number;
  pageIndex?: number;
  mode?: "interactive" | "editor";
  onRequireName?: (action: () => void, commentText?: string) => void;
  onModuleClick?: (data: any) => void;
  onEmptyClick?: () => void;
  onInsertClick?: (target: EditorInsertTarget) => void;
  activeInsertTarget?: EditorInsertTarget | null;
  selectedModuleId?: string | null;
  selectedModuleLoading?: boolean;
}) {
  const [modules, setModules] = useState(() => initModules || []);
  const moduleListRef = useRef<HTMLDivElement>(null);
  const scrollbarPressRef = useRef(false);
  // Pages are exactly viewport-height and scroll-snapped, so a low threshold would
  // open the gate mid-swipe; 0.6 lands just before the snap settles, and anything
  // near 1 can never be reached once a mobile toolbar shrinks the dvh viewport.
  const pageRevealed = useInView(moduleListRef, { once: true, amount: 0.6 });

  // The viewer mounts only a window of pages, so the latch above dies whenever
  // this page scrolls far enough away. Without the shared memory every return
  // replays the reveal.
  const revealMemory = useRevealMemory();
  const revealKey = `${slideId}-${pageIndex ?? 0}`;
  const revealState: ModuleRevealGateState = revealMemory.hasRevealed(revealKey)
    ? "done"
    : pageRevealed
      ? "revealing"
      : "held";

  useEffect(() => {
    if (pageRevealed) {
      revealMemory.markRevealed(revealKey);
    }
  }, [pageRevealed, revealKey, revealMemory]);

  useEffect(() => {
    if (!initModules) return;
    setModules(initModules);
  }, [initModules]);

  const isCasualSlide =
    slideId &&
    !["intro", "toc", "key-takeaways", "faq", "feedback"].includes(slideId);

  const handleModuleClick = (moduleId: string) => {
    const module = modules.find((m: any) => m.id === moduleId);
    if (module && onModuleClick) {
      onModuleClick(module);
    }
  };

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div
        className={cn(
          "px-5 flex flex-col justify-between h-full relative overflow-hidden ",
          "rounded-[2rem]",
        )}
        style={{
          paddingTop: HEADER_HEIGHT,
          paddingBottom: footerHeightFor(slideId),
        }}
      >
        {(() => {
          const isEditable = mode === "editor" && isCasualSlide;
          const isEmpty = !modules || modules.length === 0;
          const centerLoneModule = centersLoneModule(modules);

          if (isEmpty && isEditable) {
            return (
              <EditorSlideDropZone slideId={slideId} pageIndex={pageIndex}>
                <EditorInsertSlot
                  slideId={slideId}
                  pageIndex={pageIndex}
                  slot={0}
                  anchor={null}
                  className="top-1/2"
                />
                <div className="flex-1 flex items-center justify-center h-full">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEmptyClick?.();
                    }}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center hover:bg-primary hover:text-white transition-colors cursor-pointer group">
                      <svg
                        className="w-5 h-5 text-neutral-400 dark:text-neutral-500 group-hover:text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </div>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500">Add a module</p>
                  </button>
                </div>
              </EditorSlideDropZone>
            );
          }

          return isEditable ? (
            <EditorSlideDropZone slideId={slideId} pageIndex={pageIndex}>
              {modules.map((module: any, index: number) => {
                const isLoading =
                  !!selectedModuleLoading && selectedModuleId === module.id;
                const isSelected = selectedModuleId === module.id;
                const previous: any = index > 0 ? modules[index - 1] : null;
                const beforeAnchor: EditorInsertAnchor | undefined = previous
                  ? previous.id !== module.id
                    ? { after: previous.id }
                    : undefined
                  : isContinuationSlice(module)
                    ? undefined
                    : { before: module.id };
                const isLast = index === modules.length - 1;
                return (
                  <div
                    key={module.id}
                    id={module.id}
                    className={cn(
                      "group/module relative",
                      !isSelected && "cursor-pointer",
                      centerLoneModule && "my-auto",
                    )}
                    onPointerDownCapture={(event) => {
                      scrollbarPressRef.current = isScrollbarPointer(event);
                    }}
                    onClickCapture={(event) => {
                      if (
                        event.target instanceof Element &&
                        event.target.closest("[data-insert-slot]")
                      ) {
                        scrollbarPressRef.current = false;
                        return;
                      }
                      const fromScrollbar =
                        scrollbarPressRef.current && event.detail > 0;
                      scrollbarPressRef.current = false;
                      if (fromScrollbar) {
                        event.stopPropagation();
                        return;
                      }
                      if (isSelected) return;
                      event.preventDefault();
                      event.stopPropagation();
                      handleModuleClick(module.id);
                    }}
                    onClick={(event) => {
                      if (isSelected) event.stopPropagation();
                    }}
                  >
                    {beforeAnchor && (
                      <EditorInsertSlot
                        slideId={slideId}
                        pageIndex={pageIndex}
                        slot={index}
                        anchor={beforeAnchor}
                        className="-top-1.5"
                        onInsertClick={onInsertClick}
                        activeInsertTarget={activeInsertTarget}
                      />
                    )}
                    <div
                      aria-hidden
                      className={cn(
                        "pointer-events-none absolute -inset-1.5 rounded-lg transition-shadow z-10",
                        isSelected
                          ? "bg-neutral-400/7"
                          : "group-hover/module:ring-1 group-hover/module:ring-primary/50 group-has-[[data-insert-slot]:hover]/module:ring-0",
                      )}
                    />
                    {isLast && (
                      <EditorInsertSlot
                        slideId={slideId}
                        pageIndex={pageIndex}
                        slot={index + 1}
                        anchor={{ after: module.id }}
                        className="-bottom-1.5"
                        onInsertClick={onInsertClick}
                        activeInsertTarget={activeInsertTarget}
                      />
                    )}
                    {module.type === "image" && (
                      <EditorImageDropTarget
                        slideId={slideId}
                        pageIndex={pageIndex}
                        moduleId={module.id}
                      />
                    )}
                    <EditorModuleDragFade moduleId={module.id}>
                      {isLoading
                        ? renderModuleSkeleton(module)
                        : renderModule(module, index)}
                    </EditorModuleDragFade>
                  </div>
                );
              })}
            </EditorSlideDropZone>
          ) : (
            <div
              ref={moduleListRef}
              className="flex flex-col gap-3 h-full "
            >
              <ModuleRevealGateProvider state={revealState}>
                {modules?.map((module, index) => {
                  const isLoading =
                    !!selectedModuleLoading && selectedModuleId === module.id;
                  const isFullHeightModule =
                    module.type === "feedback" || module.type === "faq";
                  return (
                    <div
                      key={module.id ?? index}
                      data-module-id={module.id}
                      data-module-type={module.type}
                      className={cn(
                        isFullHeightModule && "flex flex-col flex-1 min-h-0",
                        centerLoneModule && "my-auto",
                      )}
                    >
                      {isLoading
                        ? renderModuleSkeleton(module)
                        : renderModule(module, index)}
                    </div>
                  );
                })}
              </ModuleRevealGateProvider>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
